import { SDK_ERRORS } from '@riz/contracts/sdk';
import { rizError, toRizError } from './errors.ts';
import {
  CLIENT_MESSAGES,
  HOST_MESSAGES,
  type HandshakeRequest,
  type HandshakeResponse,
  type Operation,
  resolveHandshake,
} from './protocol.ts';

export interface Transport {
  readonly kind: 'postmessage' | 'direct' | 'demo';
  connect(request: HandshakeRequest): Promise<HandshakeResponse>;
  send(operation: Operation): Promise<Record<string, unknown>>;
  /** Best-effort delivery during page teardown; never awaited. */
  beacon(operations: Operation[]): void;
  /** Fire-and-forget host notifications that exist before a session does. */
  notify(type: string, payload: Record<string, unknown>): void;
  onSignal(handler: (signal: string, detail: Record<string, unknown>) => void): void;
  dispose(): void;
}

const DEFAULT_TIMEOUT_MS = 15_000;
const HANDSHAKE_TIMEOUT_MS = 12_000;
const HELLO_RETRY_MS = 500;

interface PendingCall {
  resolve: (value: Record<string, unknown>) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * The community-game transport. Every call crosses `postMessage` to the Riz
 * host wrapper, which holds the run token and makes the authenticated request.
 * The creator's frame therefore never carries a Riz credential, which is the
 * property the whole embedding model rests on.
 */
export class PostMessageTransport implements Transport {
  readonly kind = 'postmessage' as const;

  private hostOrigin: string | null = null;
  private readonly allowedHostOrigins: readonly string[];
  private readonly pending = new Map<string, PendingCall>();
  private signalHandler: ((signal: string, detail: Record<string, unknown>) => void) | null = null;
  private sequence = 0;
  private handshake: {
    resolve: (value: HandshakeResponse) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
    retry: ReturnType<typeof setInterval>;
  } | null = null;

  private readonly view: Window;
  private readonly receive = (event: MessageEvent) => this.handleMessage(event);

  constructor(view: Window, allowedHostOrigins: readonly string[] = []) {
    this.view = view;
    this.allowedHostOrigins = allowedHostOrigins;
    this.view.addEventListener('message', this.receive);
  }

  connect(request: HandshakeRequest): Promise<HandshakeResponse> {
    return new Promise<HandshakeResponse>((resolve, reject) => {
      const hello = () => {
        this.post({ type: CLIENT_MESSAGES.hello, ...request }, true);
        this.post({ type: CLIENT_MESSAGES.requestSession, gameKey: request.gameKey }, true);
      };
      this.handshake = {
        resolve,
        reject,
        retry: setInterval(hello, HELLO_RETRY_MS),
        timer: setTimeout(() => {
          this.failHandshake(rizError(
            SDK_ERRORS.notConnected,
            'No Riz host responded. Launch this game from its Riz page, or develop with { sandbox: true }.',
          ));
        }, HANDSHAKE_TIMEOUT_MS),
      };
      hello();
    });
  }

  send(operation: Operation): Promise<Record<string, unknown>> {
    const requestId = `riz_${(this.sequence += 1)}_${Date.now().toString(36)}`;
    return new Promise<Record<string, unknown>>((resolve, reject) => {
      const delivered = this.post({
        type: CLIENT_MESSAGES.operation,
        requestId,
        kind: operation.kind,
        name: operation.name,
        payload: operation.payload,
        eventId: operation.eventId,
        seq: operation.seq,
      });
      if (!delivered) {
        reject(rizError(SDK_ERRORS.notConnected, 'Lost the connection to the Riz host frame.'));
        return;
      }
      this.pending.set(requestId, {
        resolve,
        reject,
        timer: setTimeout(() => {
          this.pending.delete(requestId);
          reject(rizError(SDK_ERRORS.timeout, 'Riz did not answer in time.'));
        }, operation.timeoutMs ?? DEFAULT_TIMEOUT_MS),
      });
    });
  }

  beacon(operations: Operation[]): void {
    if (!operations.length) return;
    this.post({
      type: CLIENT_MESSAGES.beacon,
      events: operations.map((operation) => ({
        event: operation.name,
        eventId: operation.eventId,
        seq: operation.seq,
        payload: operation.payload,
      })),
    });
  }

  notify(type: string, payload: Record<string, unknown>): void {
    this.post({ type, ...payload }, true);
  }

  onSignal(handler: (signal: string, detail: Record<string, unknown>) => void): void {
    this.signalHandler = handler;
  }

  dispose(): void {
    this.view.removeEventListener('message', this.receive);
    this.clearHandshake();
    for (const [, call] of this.pending) {
      clearTimeout(call.timer);
      call.reject(rizError(SDK_ERRORS.sessionInactive, 'The Riz session was disposed.'));
    }
    this.pending.clear();
  }

  private post(message: Record<string, unknown>, allowBroadcast = false): boolean {
    const target = this.hostOrigin ?? this.allowedHostOrigins[0] ?? (allowBroadcast ? '*' : null);
    if (!target) return false;
    try {
      this.view.parent.postMessage(message, target);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Two independent checks: the sender must be our immediate parent window, and
   * its origin must match the one we already bound to. Either alone is
   * bypassable — origin can be spoofed by an opener chain, and a window
   * reference alone says nothing about who now occupies it.
   */
  private trusted(event: MessageEvent): boolean {
    const data = event.data as { type?: unknown } | null;
    if (!data || typeof data.type !== 'string' || !data.type.startsWith('riz:')) return false;
    try {
      if (event.source !== this.view.parent) return false;
    } catch {
      return false;
    }
    if (this.allowedHostOrigins.length && !this.allowedHostOrigins.includes(event.origin)) return false;
    if (this.hostOrigin && event.origin !== this.hostOrigin) return false;
    return true;
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.trusted(event)) return;
    if (!this.hostOrigin) this.hostOrigin = event.origin;
    const message = event.data as Record<string, unknown> & { type: string };

    switch (message.type) {
      case HOST_MESSAGES.hostReady:
        return;
      case HOST_MESSAGES.session:
        this.completeHandshake(message);
        return;
      case HOST_MESSAGES.error:
        this.failHandshake(toRizError(message));
        return;
      case HOST_MESSAGES.result:
        this.settle(message);
        return;
      case HOST_MESSAGES.signal:
        this.signalHandler?.(String(message.signal ?? ''), (message.detail as Record<string, unknown>) ?? {});
        return;
      default:
        return;
    }
  }

  private completeHandshake(message: Record<string, unknown>): void {
    const resolved = resolveHandshake(message);
    if (!resolved) {
      this.failHandshake(rizError(
        SDK_ERRORS.protocolUnsupported,
        'This Riz host does not speak a protocol version this SDK supports.',
      ));
      return;
    }
    const handshake = this.handshake;
    this.clearHandshake();
    handshake?.resolve(resolved);
  }

  private failHandshake(error: Error): void {
    const handshake = this.handshake;
    this.clearHandshake();
    handshake?.reject(error);
  }

  private clearHandshake(): void {
    if (!this.handshake) return;
    clearTimeout(this.handshake.timer);
    clearInterval(this.handshake.retry);
    this.handshake = null;
  }

  private settle(message: Record<string, unknown>): void {
    const requestId = String(message.requestId ?? '');
    const call = this.pending.get(requestId);
    if (!call) return;
    this.pending.delete(requestId);
    clearTimeout(call.timer);

    if (message.ok === true) {
      call.resolve((message.data ?? message.result ?? {}) as Record<string, unknown>);
      return;
    }
    call.reject(toRizError(message.error ?? message.result ?? message));
  }
}

/**
 * The first-party transport. A Riz-hosted title is same-origin with the API and
 * authenticates by cookie, so there is no wrapper to relay through.
 */
export class DirectTransport implements Transport {
  readonly kind = 'direct' as const;

  private signalHandler: ((signal: string, detail: Record<string, unknown>) => void) | null = null;
  private readonly baseUrl: string;

  constructor(baseUrl = '') {
    this.baseUrl = baseUrl;
  }

  async connect(request: HandshakeRequest): Promise<HandshakeResponse> {
    const raw = await this.post('/api/sdk/session', {
      gameKey: request.gameKey,
      sdkVersion: request.sdkVersion,
      protocol: request.protocol,
      frameOrigin: request.frameOrigin,
    });
    const resolved = resolveHandshake(raw);
    if (!resolved) {
      throw rizError(SDK_ERRORS.protocolUnsupported, 'Riz does not speak a protocol version this SDK supports.');
    }
    return resolved;
  }

  send(operation: Operation): Promise<Record<string, unknown>> {
    const route = operation.kind === 'event'
      ? '/api/sdk/events'
      : operation.kind === 'query' ? '/api/sdk/query' : `/api/sdk/${operation.name}`;
    const body = operation.kind === 'event'
      ? { event: operation.name, eventId: operation.eventId, seq: operation.seq, payload: operation.payload }
      : operation.kind === 'query'
        ? { query: operation.name, params: operation.payload }
        : operation.payload;
    return this.post(route, body);
  }

  beacon(operations: Operation[]): void {
    if (!operations.length || typeof navigator === 'undefined') return;
    const body = JSON.stringify({
      events: operations.map((operation) => ({
        event: operation.name,
        eventId: operation.eventId,
        seq: operation.seq,
        payload: operation.payload,
      })),
    });
    const sent = navigator.sendBeacon?.(
      `${this.baseUrl}/api/sdk/events`,
      new Blob([body], { type: 'application/json' }),
    );
    if (sent) return;
    void fetch(`${this.baseUrl}/api/sdk/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  }

  notify(): void {
    // A first-party title is the host; there is nobody to notify.
  }

  onSignal(handler: (signal: string, detail: Record<string, unknown>) => void): void {
    this.signalHandler = handler;
  }

  dispose(): void {
    this.signalHandler = null;
  }

  private async post(path: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
    } catch (error) {
      throw rizError(SDK_ERRORS.notConnected, 'Riz is unreachable right now.', error);
    }
    const data = await response.json().catch(() => ({})) as Record<string, unknown>;
    if (!response.ok) throw toRizError(data);
    return data;
  }
}
